import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Paper,
  Divider,
  Fade,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Added for expiry icon
import { BASE_URL } from '../global/Server/endpoints';
import { ReactComponent as Logo } from '../../components/header/nube-data-black.svg';
import './Customer.css';

const CustomerClaimPage = () => {
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [passData, setPassData] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');

  const GOOGLE_WALLET_BADGE =
    'https://upload.wikimedia.org/wikipedia/commons/b/bb/Add_to_Google_Wallet_badge.svg';
  const APPLE_WALLET_BADGE =
    'https://upload.wikimedia.org/wikipedia/commons/3/30/Add_to_Apple_Wallet_badge.svg';

  // Fixed Countdown Logic
  const calculateTimeLeft = useCallback((expiryDate) => {
    if (!expiryDate) return;

    const difference = new Date(expiryDate).getTime() - new Date().getTime();

    if (difference <= 0) {
      setStatus('error');
      return;
    }

    const hours = Math.floor(difference / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    // Prevent NaN display if date parsing fails initially
    if (isNaN(hours) || isNaN(minutes)) {
      setTimeLeft('');
    } else {
      setTimeLeft(`${hours}h ${minutes}m remaining`);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      return;
    }

    fetch(`${BASE_URL}/wallet/claimDetails?token=${token}`)
      .then((res) => {
        if (!res.ok) throw new Error('Backend Error');
        return res.json();
      })
      .then((data) => {
        localStorage.setItem('nube_customer_phone', data.phone);
        setPassData(data);
        setStatus('success');

        // Start countdown
        calculateTimeLeft(data.expiresAt);
        const timer = setInterval(
          () => calculateTimeLeft(data.expiresAt),
          60000
        );
        return () => clearInterval(timer);
      })
      .catch(() => setStatus('error'));
  }, [location.search, calculateTimeLeft]);

  // Reliable download handler for mobile browsers
  const handleDownload = (e) => {
    // For .pkpass files, window.location.href is more reliable on iOS
    e.preventDefault();
    window.location.href = passData.passUrl;
  };

  if (status === 'loading')
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#001665',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
        <Typography sx={{ mt: 2, color: 'white', opacity: 0.7 }}>
          Verifying Security Token...
        </Typography>
      </Box>
    );

  if (status === 'error')
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#001665',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <Paper
          sx={{ p: 4, textAlign: 'center', borderRadius: 4, maxWidth: 350 }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Link Expired
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This security link has expired. For your safety, pass links are only
            valid for 24 hours.
          </Typography>
        </Paper>
      </Box>
    );

  return (
    <Fade in={true} timeout={800}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #001665 0%, #000b33 100%)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Branding */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 6, mb: 4 }}
        >
          <div className="login-top-claim">
            <Logo className="login-logo-claim" />
            <Typography variant="h5" className="login-brand">
              NuVe System
            </Typography>
          </div>
        </Stack>

        <Paper
          sx={{
            borderRadius: 6,
            width: '100%',
            maxWidth: 380,
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
            bgcolor: 'white',
          }}
        >
          <Box
            sx={{
              height: 8,
              background: 'linear-gradient(90deg, #001665, #00e676)',
            }}
          />

          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 50, color: '#001665', mb: 2, opacity: 0.8 }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#1a1a1a',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              Pass Ready
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#001665', fontWeight: 500, mb: 1 }}
            >
              {passData.customerName}
            </Typography>

            {/* EXPIRY DISPLAY - Added here */}
            {timeLeft && (
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 3, color: '#d32f2f' }}
              >
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                >
                  {timeLeft.toUpperCase()}
                </Typography>
              </Stack>
            )}

            <Divider sx={{ mb: 4 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontWeight: 700, px: 1 }}
              >
                INSTALLATION
              </Typography>
            </Divider>

            <Box
              onClick={handleDownload} // Using a handler for better mobile behavior
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { transform: 'translateY(-2px)' },
                '&:active': { transform: 'scale(0.96)' },
              }}
            >
              <img
                src={
                  passData.walletType === 'google'
                    ? GOOGLE_WALLET_BADGE
                    : APPLE_WALLET_BADGE
                }
                alt="Add to Wallet"
                style={{
                  height: '52px',
                  width: 'auto',
                  display: 'block',
                  borderRadius: '8px',
                  margin: '0 auto',
                }}
              />
            </Box>

            <Typography
              variant="body2"
              sx={{ color: '#666', mt: 4, px: 2, lineHeight: 1.5 }}
            >
              Tap the button above to store your encrypted security key in your
              mobile wallet.
            </Typography>
          </Box>
        </Paper>

        <Box
          sx={{
            mt: 'auto',
            mb: 4,
            textAlign: 'center',
            opacity: 0.5,
            marginTop: '32px',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'white', display: 'block' }}
          >
            NUVE SECURE ACCESS SYSTEM
          </Typography>
          <Typography variant="caption" sx={{ color: 'white' }}>
            Device Bound • Encrypted
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
};

export default CustomerClaimPage;
